import {IMonteCarloResults} from "../../../../components/calculator/Calculator";
import {Box, FormLabel, Typography, useTheme} from "@mui/material";
import {numberToPercentFormat} from "../../../../stores/StoreUtilities";
import RateModels from "../../../../components/calculator/RateModels";
import React from "react";

const RetirementConfidenceWidget = ({
  monteCarloResults,
  width
} : {
  monteCarloResults: IMonteCarloResults
  width?: string
}) => {
  const theme = useTheme()

  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"],
    },
    comparisonBarGraph: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      marginBottom: "5px",
    },
    comparisonBars: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      height: 28,
      marginRight: "4px",
      marginTop: "0px"
    },
    comparisonBar: {
      display: "flex",
      flexDirection: "row",
      height: 20,
      width: "100%",
      marginBottom: "2px"
    },
    comparisonBarSegment: {
      display: "inline-flex",
      justifyContent: "flex-end",
      alignItems: "center",
      height: 20,
      fontSize: 13,
      overflowX: "hidden",
    },
    benchmarkBarSegmentToRight: {
      display: "inline-flex",
      justifyContent: "flex-end",
      alignItems: "center",
      height: 20,
      fontSize: 13,
      overflowX: "hidden",
      backgroundImage: "linear-gradient(to right, #AD2D1E40, #5A7D4A40)"
    },
    benchmarkBarSegmentToLeft: {
      display: "inline-flex",
      justifyContent: "flex-end",
      alignItems: "center",
      height: 20,
      fontSize: 13,
      overflowX: "hidden",
      backgroundImage: "linear-gradient(to left, #AD2D1E40, #5A7D4A40)"
    },
    confidenceBar: {
      display: "inline-flex",
      justifyContent: "flex-end",
      alignItems: "center",
      height: 20,
      fontSize: 13,
      overflowX: "hidden",
      backgroundImage: "linear-gradient(to left, #5A7D4AE0,#AD2D1EE0)"
    },
    benchmarkBarLabel: {
      display: "inline-flex",
      paddingLeft: "5px",
      alignItems: "center",
      fontSize: 14,
      color: "#18427680",
    },
    comparisonBarLabel: {
      display: "inline-flex",
      paddingLeft: "5px",
      alignItems: "center",
      fontSize: 14,
      color: "#184276",
      fontWeight: 600
    }
  }

  const interpolateColor = (color1: string, color2: string, factor: number) =>{
    // Parse RGB values (assuming hex colors like #FF0000)
    const hex1 = color1.replace('#', '');
    const hex2 = color2.replace('#', '');

    const r1 = parseInt(hex1.substr(0, 2), 16);
    const g1 = parseInt(hex1.substr(2, 2), 16);
    const b1 = parseInt(hex1.substr(4, 2), 16);

    const r2 = parseInt(hex2.substr(0, 2), 16);
    const g2 = parseInt(hex2.substr(2, 2), 16);
    const b2 = parseInt(hex2.substr(4, 2), 16);

    // Interpolate each channel
    const r = Math.round(r1 + (r2 - r1) * factor);
    const g = Math.round(g1 + (g2 - g1) * factor);
    const b = Math.round(b1 + (b2 - b1) * factor);

    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  const renderBarGraph = (title: string, value: number, greaterIsBetter: boolean = true) => {
    const benchmark = 1
    const max = Math.max(benchmark, value)
    const comparisonPct = numberToPercentFormat(value / max, 0)
    const comparisonLabel = numberToPercentFormat(value, 1)

    const endColor = interpolateColor("#AD2D1EE0", "#5A7D4A", value)
    const backgroundImage = `linear-gradient(to right, #AD2D1E80, ${endColor})`

    return (
      <Box sx={styles.comparisonBarGraph} width={width}>
        <FormLabel sx={styles.formLabel}>{title}</FormLabel>
        <Box sx={styles.comparisonBars}>
          {/*<Box sx={styles.comparisonBar}>*/}
          {/*  <Box sx={benchmarkSegmentStyle} width={benchmarkPct}></Box><Box sx={styles.benchmarkBarLabel}>{benchmarkLabel}</Box>*/}
          {/*</Box>*/}
          <Box sx={styles.comparisonBar}>
            <Box sx={{...styles.confidenceBar, backgroundImage}} width={comparisonPct}></Box><Box sx={styles.comparisonBarLabel}>{comparisonLabel}</Box>
          </Box>
        </Box>
      </Box>
    )
  }

  const rateModels = new RateModels()

  return (
    <Box>
      <Box display="flex" flexDirection="column" flexGrow={0}>
        {renderBarGraph("Retirement Confidence Score", monteCarloResults.confidenceScore, true)}
      </Box>
      <Typography variant="body2" sx={{lineHeight: 1.3}} gutterBottom>
        You have a <b>{numberToPercentFormat(monteCarloResults.confidenceScore, 0)}</b> chance of fully funding your retirement using
        <em> {rateModels.getRateModelLabel(monteCarloResults.rateModel)}</em> portfolio assumptions.
      </Typography>
      <Typography variant="body2" sx={{fontSize: 13}}>
        In this Monte Carlo projection, we ran <b>{monteCarloResults.simulations}</b> simulations based on your projected budget, taxes, historic market volatility and annual rebalancing assumptions.  Your chance of success is determined by the percentage of these simulations in which you successfully reach expected longevity age without depleting your funds.
      </Typography>
    </Box>
  )
}

export default RetirementConfidenceWidget